// src/components/order/ModalCreateOrder.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import Modal from "../common/Modal";
import OrderCustomSelect from "./OrderCustomSelect";
import JsonSchemaForm from "../common/JsonSchemaForm";
import { getOrderSchemas, getERPData, getERPPartes } from "../../common/api/orders";
import { getCurrentUsername } from "../../common/api/auth";
import { getUserByUsername, getUsersByClient } from "../../common/api/users";
import { getGroups, getGroupByName } from "../../common/api/groups";
import { useToast } from "../../hooks/useToast";
import { validateJsonSchemaFormData } from "../../utils/jsonSchema";

const EMPTY_VALUE = "";

function normalizeSchemasResponse(rawSchemas) {
  if (!rawSchemas) return [];

  const schemaItems = Array.isArray(rawSchemas)
    ? rawSchemas
    : rawSchemas.schemas || rawSchemas.Schemas || rawSchemas.data || [];

  return schemaItems
    .map((item) => {
      if (!item) return null;

      const id =
        item.TipoParte ??
        item.tipoParte ??
        item.ID ??
        item.id ??
        item.code ??
        item.codigo ??
        item.value;

      const schema = item.Schema ?? item.schema ?? item.jsonSchema ?? null;

      if (!id || !schema || typeof schema !== "object") {
        return null;
      }

      const label =
        item.Descripcion ??
        item.descripcion ??
        item.description ??
        item.label ??
        schema.title ??
        schema.description ??
        `Tipo ${id}`;

      const paramsOrder =
        item.paramsOrder ??
        item.order ??
        schema.paramsOrder ??
        (schema.properties ? Object.keys(schema.properties) : []);

      return {
        id: String(id),
        label,
        schema,
        paramsOrder: Array.isArray(paramsOrder) ? paramsOrder : []
      };
    })
    .filter(Boolean);
}

function getFirstDefined(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return undefined;
}

function resolveValueFromMeter(meter, key) {
  if (!meter) return undefined;

  const { partes = {}, erp = {}, base = {} } = meter;
  return getFirstDefined(partes[key], erp[key], base[key]);
}

function formatParamValue(value, propertySchema) {
  if (value === undefined || value === null) {
    return "";
  }

  if (propertySchema?.type === "boolean") {
    if (typeof value === "string") {
      const normalized = value.toLowerCase();
      return normalized === "true" || normalized === "si" || normalized === "1" ? "SI" : "NO";
    }
    return value ? "SI" : "NO";
  }

  if (propertySchema?.type === "number" || propertySchema?.type === "integer") {
    const numeric = Number(value);
    if (Number.isNaN(numeric)) {
      return "";
    }
    return numeric.toString();
  }

  if (Array.isArray(value)) {
    return value.map((item) => String(item)).join(",");
  }

  return String(value);
}

function computeDefaultFormData(schema, meter) {
  if (!schema?.properties) {
    return {};
  }

  return Object.keys(schema.properties).reduce((acc, key) => {
    const propertySchema = schema.properties[key];
    const valueFromMeter = resolveValueFromMeter(meter, key);

    if (valueFromMeter !== undefined) {
      acc[key] = valueFromMeter;
    } else if (propertySchema.default !== undefined) {
      acc[key] = propertySchema.default;
    }

    return acc;
  }, {});
}

function shallowEqual(objA = {}, objB = {}) {
  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);

  if (keysA.length !== keysB.length) return false;

  return keysA.every((key) => objA[key] === objB[key]);
}

function ModalCreateOrder({ erpData, clientCodId, onClose, onSave }) {
  const toast = useToast();

  const [schemas, setSchemas] = useState([]);
  const [selectedSchemaId, setSelectedSchemaId] = useState(EMPTY_VALUE);
  const [formData, setFormData] = useState({});
  const [assignmentMode, setAssignmentMode] = useState("user");
  const [users, setUsers] = useState([]);
  const [userFilter, setUserFilter] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(EMPTY_VALUE);
  const [groups, setGroups] = useState([]);
  const [groupFilter, setGroupFilter] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState(EMPTY_VALUE);
  const [meters, setMeters] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const previousSchemaIdRef = useRef(null);

  const selectedSchema = useMemo(
    () => schemas.find((schemaItem) => schemaItem.id === selectedSchemaId) || null,
    [schemas, selectedSchemaId]
  );

  const filteredUsers = useMemo(() => {
    if (!userFilter) return users;
    const normalized = userFilter.toLowerCase();
    return users.filter((user) => {
      const text = `${user.USER || ""} ${user.NOMBRE || ""} ${user.COD_ID || ""}`.toLowerCase();
      return text.includes(normalized);
    });
  }, [users, userFilter]);

  const filteredGroups = useMemo(() => {
    if (!groupFilter) return groups;
    const normalized = groupFilter.toLowerCase();
    return groups.filter((group) => {
      const text = `${group.NOMBRE || ""} ${group.COD_ID || ""}`.toLowerCase();
      return text.includes(normalized);
    });
  }, [groups, groupFilter]);

  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const username = getCurrentUsername();
        if (!username) return;

        const user = await getUserByUsername(username);
        setCurrentUser(user);

        if (user?.COD_ID) {
          setSelectedUserId(String(user.COD_ID));
        }
      } catch (error) {
        console.error("Error al obtener usuario actual", error);
      }
    };

    loadCurrentUser();
  }, []);

  useEffect(() => {
    const loadInitialData = async () => {
      if (!clientCodId) {
        toast.error("Falta el identificador del cliente para crear partes");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const schemasPromise = getOrderSchemas();
        const usersPromise = getUsersByClient(clientCodId).catch((error) => {
          console.warn("No se pudieron cargar usuarios del cliente", error);
          return [];
        });
        const groupsPromise = getGroups().catch((error) => {
          console.warn("No se pudieron cargar grupos", error);
          return [];
        });
        const metersPromise = Promise.all(
          (erpData || []).map(async (item) => {
            const cups = item.CUPS || item.cups || item.Cups;

            try {
              const [partesData, erpInfoData] = await Promise.all([
                getERPPartes(clientCodId, cups).catch((error) => {
                  console.warn(`No se pudieron cargar datos de /erp/partes para ${cups}`, error);
                  return null;
                }),
                getERPData(cups).catch((error) => {
                  console.warn(`No se pudieron cargar datos de /erp para ${cups}`, error);
                  return null;
                })
              ]);

              const partesRecord = Array.isArray(partesData) ? partesData[0] : partesData;
              const erpRecord = Array.isArray(erpInfoData) ? erpInfoData[0] : erpInfoData;

              return {
                cups,
                base: item,
                partes: partesRecord || {},
                erp: erpRecord || {}
              };
            } catch (error) {
              console.error(`Error al cargar datos ERP para ${cups}`, error);
              return {
                cups,
                base: item,
                partes: {},
                erp: {}
              };
            }
          })
        );

        const [schemasResponse, usersResponse, groupsResponse, metersData] = await Promise.all([
          schemasPromise,
          usersPromise,
          groupsPromise,
          metersPromise
        ]);

        const normalizedSchemas = normalizeSchemasResponse(schemasResponse);
        setSchemas(normalizedSchemas);

        if (!selectedSchemaId && normalizedSchemas.length > 0) {
          setSelectedSchemaId(normalizedSchemas[0].id);
        }

        const normalizedUsers = Array.isArray(usersResponse) ? usersResponse : [];
        setUsers(normalizedUsers);

        if (!selectedUserId && normalizedUsers.length > 0) {
          const firstUser = normalizedUsers[0];
          const userId = firstUser?.COD_ID ?? firstUser?.cod_id ?? firstUser?.id ?? firstUser?.USER;
          if (userId) {
            setSelectedUserId(String(userId));
          }
        }

        const groupsRaw = Array.isArray(groupsResponse) ? groupsResponse : [];
        const groupDetails = await Promise.all(
          groupsRaw.map(async (groupItem) => {
            if (!groupItem) return null;

            if (groupItem.COD_ID) {
              return groupItem;
            }

            const groupName = groupItem.NOMBRE || groupItem.GRUPO || groupItem.name || groupItem;

            if (!groupName) return null;

            try {
              return await getGroupByName(groupName);
            } catch (error) {
              console.warn(`No se pudo obtener detalle del grupo ${groupName}`, error);
              return null;
            }
          })
        );

        const filteredGroupDetails = groupDetails
          .filter(Boolean)
          .filter((group) => String(group.COD_CLIENTE || group.COD_ID_GROUP || clientCodId) === String(clientCodId));

        setGroups(filteredGroupDetails);

        if (!selectedGroupId && filteredGroupDetails.length > 0) {
          const firstGroupId = filteredGroupDetails[0]?.COD_ID;
          if (firstGroupId) {
            setSelectedGroupId(String(firstGroupId));
          }
        }

        setMeters(metersData);
      } catch (error) {
        console.error("Error al cargar datos iniciales para crear partes", error);
        toast.error("Error al preparar la creación de partes");
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientCodId, JSON.stringify(erpData)]);

  useEffect(() => {
    if (!selectedSchema) return;

    const baseMeter = meters[0];
    const defaults = computeDefaultFormData(selectedSchema.schema, baseMeter);

    setFormData((prev) => {
      const merged = { ...defaults, ...(prev || {}) };
      const shouldUpdate =
        previousSchemaIdRef.current !== selectedSchema.id || !shallowEqual(prev || {}, merged);

      if (shouldUpdate) {
        previousSchemaIdRef.current = selectedSchema.id;
        return merged;
      }

      return prev;
    });
  }, [selectedSchema, meters]);

  const handleAssignmentChange = (newMode) => {
    setAssignmentMode(newMode);

    if (newMode === "user") {
      setSelectedGroupId(EMPTY_VALUE);
    } else {
      setSelectedUserId(EMPTY_VALUE);
    }
  };

  const buildParamsForMeter = (meter) => {
    if (!selectedSchema?.schema?.properties) {
      return [];
    }

    const propertyKeys = selectedSchema.paramsOrder.length
      ? selectedSchema.paramsOrder
      : Object.keys(selectedSchema.schema.properties);

    return propertyKeys.map((key) => {
      const propertySchema = selectedSchema.schema.properties[key] || {};
      const valueFromMeter = resolveValueFromMeter(meter, key);
      const valueFromForm = formData[key];
      const selectedValue = getFirstDefined(valueFromMeter, valueFromForm, propertySchema.default);
      return formatParamValue(selectedValue, propertySchema);
    });
  };

  const buildOrderPayload = (meter) => {
    const base = meter.base || {};
    const partes = meter.partes || {};
    const erp = meter.erp || {};

    const order = {
      COD_ID_GROUP: clientCodId,
      TipoParte: selectedSchema?.id || EMPTY_VALUE,
      CUPS: meter.cups || base.CUPS || partes.CUPS || erp.CUPS || EMPTY_VALUE,
      CntId: getFirstDefined(partes.CntId, base.CntId, base.NS, erp.CntId, erp.NS, EMPTY_VALUE),
      NOMBRECT: getFirstDefined(partes.NOMBRECT, base.NOMBRECT, erp.NOMBRECT, EMPTY_VALUE),
      DIRECCIONSUMINISTRO: getFirstDefined(
        partes.DIRECCIONSUMINISTRO,
        base.DIRECCIONSUMINISTRO,
        erp.DIRECCIONSUMINISTRO,
        EMPTY_VALUE
      ),
      RUTA: getFirstDefined(partes.CODTRAFO, base.CODTRAFO, erp.CODTRAFO, EMPTY_VALUE),
      Te: getFirstDefined(partes.Te, base.Te, erp.Te, "5"),
      TIPO_INSTALACION: getFirstDefined(
        partes.TIPO_INSTALACION,
        base.TIPO_INSTALACION,
        partes.TIPO_INSTALACION_1,
        erp.TIPO_INSTALACION,
        "Monofasica"
      ),
      COORDENADA_X: getFirstDefined(partes.COORDENADA_X, base.COORDENADA_X, erp.COORDENADA_X, "0"),
      COORDENADA_Y: getFirstDefined(partes.COORDENADA_Y, base.COORDENADA_Y, erp.COORDENADA_Y, "0"),
      PARAMS: buildParamsForMeter(meter)
    };

    if (assignmentMode === "user") {
      order.COD_ID_USER = selectedUserId;
    } else {
      order.COD_ID_USER_GROUP = selectedGroupId;
    }

    return order;
  };

  const handleSave = async () => {
    if (!selectedSchema) {
      toast.error("Selecciona el tipo de parte");
      return;
    }

    if (assignmentMode === "user" && !selectedUserId) {
      toast.error("Selecciona un usuario o técnico");
      return;
    }

    if (assignmentMode === "group" && !selectedGroupId) {
      toast.error("Selecciona un grupo de usuarios");
      return;
    }

    if (!meters.length) {
      toast.error("No hay CUPS seleccionados para crear partes");
      return;
    }

    const validationErrors = validateJsonSchemaFormData(selectedSchema.schema, formData);
    if (validationErrors.length > 0) {
      toast.error(`Revisa el formulario: ${validationErrors[0].key} - ${validationErrors[0].message}`);
      return;
    }

    try {
      setSaving(true);
      const ordersToCreate = meters.map(buildOrderPayload);
      const success = await onSave(ordersToCreate);

      if (success) {
        onClose();
      }
    } catch (error) {
      console.error("Error al guardar partes", error);
    } finally {
      setSaving(false);
    }
  };

  const metersTable = useMemo(
    () =>
      meters.map((meter) => {
        const base = meter.base || {};
        const partes = meter.partes || {};
        const erp = meter.erp || {};

        return {
          cups: meter.cups || base.CUPS || partes.CUPS || erp.CUPS,
          cntId: getFirstDefined(partes.CntId, base.CntId, base.NS, erp.CntId, erp.NS, "-"),
          nombre: getFirstDefined(partes.NOMBRECT, base.NOMBRECT, erp.NOMBRECT, "-"),
          direccion: getFirstDefined(
            partes.DIRECCIONSUMINISTRO,
            base.DIRECCIONSUMINISTRO,
            erp.DIRECCIONSUMINISTRO,
            "-"
          )
        };
      }),
    [meters]
  );

  return (
    <Modal title="Crear Partes de Trabajo" onClose={onClose} className="modal-large">
      <div className="modal-create-order">
        {loading ? (
          <div className="input-group modal-create-order__body" style={{ textAlign: "center", padding: "2rem" }}>
            <p>Cargando datos para crear el parte...</p>
          </div>
        ) : (
          <>
            <div className="modal-create-order__body">
              <div className="input-group">
                <label>CUPS seleccionados: {meters.length}</label>
                <div
                  style={{
                    maxHeight: "160px",
                    overflow: "auto",
                    border: "1px solid #ccc",
                    padding: "0.5rem",
                    borderRadius: "4px"
                  }}
                >
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: "left", padding: "0.25rem" }}>CUPS</th>
                        <th style={{ textAlign: "left", padding: "0.25rem" }}>CntID</th>
                        <th style={{ textAlign: "left", padding: "0.25rem" }}>Nombre CT</th>
                        <th style={{ textAlign: "left", padding: "0.25rem" }}>Dirección suministro</th>
                      </tr>
                    </thead>
                    <tbody>
                      {metersTable.map((meterRow) => (
                        <tr key={meterRow.cups}>
                          <td style={{ padding: "0.25rem", borderTop: "1px solid #eee" }}>{meterRow.cups}</td>
                          <td style={{ padding: "0.25rem", borderTop: "1px solid #eee" }}>{meterRow.cntId}</td>
                          <td style={{ padding: "0.25rem", borderTop: "1px solid #eee" }}>{meterRow.nombre}</td>
                          <td style={{ padding: "0.25rem", borderTop: "1px solid #eee" }}>{meterRow.direccion}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="input-group">
                <label>Tipo de parte *</label>
                <OrderCustomSelect
                  options={schemas.map((schemaItem) => ({
                    value: schemaItem.id,
                    label: schemaItem.label
                  }))}
                  value={selectedSchemaId}
                  onChange={(value) => setSelectedSchemaId(value)}
                  placeholder="-- Seleccionar tipo de parte --"
                />
                {selectedSchema?.schema?.description && (
                  <small style={{ color: "#666" }}>{selectedSchema.schema.description}</small>
                )}
              </div>

              {selectedSchema ? (
                <div className="input-group">
                  <label>Datos necesarios para el parte</label>
                  <JsonSchemaForm schema={selectedSchema.schema} formData={formData} onChange={setFormData} />
                </div>
              ) : (
                <div className="input-group">
                  <p style={{ color: "#666" }}>Selecciona el tipo de parte para cargar el formulario.</p>
                </div>
              )}

              <div className="input-group">
                <label>Asignar a *</label>
                <div style={{ display: "flex", gap: "1rem", marginBottom: "0.5rem" }}>
                  <label>
                    <input
                      type="radio"
                      value="user"
                      checked={assignmentMode === "user"}
                      onChange={(event) => handleAssignmentChange(event.target.value)}
                    />
                    Usuario / Técnico
                  </label>
                  <label>
                    <input
                      type="radio"
                      value="group"
                      checked={assignmentMode === "group"}
                      onChange={(event) => handleAssignmentChange(event.target.value)}
                    />
                    Grupo de usuarios
                  </label>
                </div>

                {assignmentMode === "user" && (
                  <>
                    <div className="input-group" style={{ paddingLeft: 0 }}>
                      <label>Filtrar usuarios o técnicos</label>
                      <input
                        type="text"
                        value={userFilter}
                        onChange={(event) => setUserFilter(event.target.value)}
                        placeholder="Buscar por nombre, usuario o COD_ID"
                      />
                    </div>
                    {filteredUsers.length > 0 ? (
                      <OrderCustomSelect
                        options={filteredUsers.map((user) => ({
                          value: String(user.COD_ID ?? user.cod_id ?? user.id ?? user.USER ?? ""),
                          label: `${user.USER || user.NOMBRE || user.COD_ID || "Usuario"} (${user.COD_ID || user.USER || ""})`
                        }))}
                        value={selectedUserId}
                        onChange={(value) => setSelectedUserId(value)}
                        placeholder="-- Seleccionar usuario o técnico --"
                      />
                    ) : (
                      <p style={{ color: "#999" }}>No hay usuarios disponibles para este cliente</p>
                    )}

                    {currentUser?.USER && (
                      <small style={{ color: "#666" }}>
                        Usuario actual: {currentUser.USER} ({currentUser.COD_ID})
                      </small>
                    )}
                  </>
                )}

                {assignmentMode === "group" && (
                  <>
                    <div className="input-group" style={{ paddingLeft: 0 }}>
                      <label>Filtrar grupos</label>
                      <input
                        type="text"
                        value={groupFilter}
                        onChange={(event) => setGroupFilter(event.target.value)}
                        placeholder="Buscar por nombre o COD_ID"
                      />
                    </div>
                    {filteredGroups.length > 0 ? (
                      <OrderCustomSelect
                        options={filteredGroups.map((group) => ({
                          value: String(group.COD_ID),
                          label: `${group.NOMBRE || "Grupo"} (${group.COD_ID})`
                        }))}
                        value={selectedGroupId}
                        onChange={(value) => setSelectedGroupId(value)}
                        placeholder="-- Seleccionar grupo --"
                      />
                    ) : (
                      <p style={{ color: "#999" }}>No hay grupos disponibles para el cliente</p>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="input-group_row modal-create-order__footer">
              <span
                className={`boton_opciones${saving ? " disable" : ""}`}
                onClick={() => {
                  if (!saving) handleSave();
                }}
              >
                {saving ? "Creando partes..." : `Crear ${meters.length} parte(s)`}
              </span>
              <span className="boton_cancelar" onClick={onClose}>
                Cancelar
              </span>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

export default ModalCreateOrder;